import { useQueryClient } from '@tanstack/react-query';
import { confirmDelete } from 'components/v2/confirm-dialog/confirmDelete';
import { CompanyContext } from 'contexts/CompanyContextProvider';
import { Menu } from 'primereact/menu';
import { MenuItem } from 'primereact/menuitem';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { EpdLinks } from 'routes/EpdRoutes';
import AuthService from 'services/AuthService';
import { EPDStatusDisplayName } from 'services/EpdClient';
import EpdService from 'services/EpdService';
import EpdStateService from 'services/EpdStateService';
import { QUERY_KEYS } from 'services/api/constants';
import { DashboardListItemModel } from 'types/dashboard/DashboardListItemModel';
import styled from 'styled-components';
import { PoorMansError } from 'util/utils';
import { Card, CardDate, CardFormatText, CardHeading, CardName, CardStatus } from './styles';
import { getCardDateText } from './cardDateText';
import Tooltip from 'components/controls/Tooltip';
import { ReactComponent as FileMarkIcon } from 'images/v2/icons/svg/fill/svg/file-mark.svg';

const COPY_SECTIONS = [
  'EpdDeveloper',
  'EpdVerifier',
  'Pcr',
  'Collections',
  'ProductDescription',
  'Products',
  'Images',
  'GeographicalScope',
  'ReferenceFlows',
  'MaterialProperties',
  'MachineReadableGeographicalScope',
  'Description',
  'TechnologyDescription',
  'TechnicalPurposeOfProduct',
  'Subtype',
  'DataSources',
  'UseAdvice',
];

type TProps = {
  epd: DashboardListItemModel;
  background: any;
};

const EpdCard = ({ epd, background }: TProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const menuRef = useRef<Menu | null>(null);
  const queryClient = useQueryClient();
  const { companyAccountId } = useContext(CompanyContext);
  const isSystemAdmin = AuthService.isSystemAdmin();
  const [isBusy, setIsBusy] = useState(false);
  const titleRef = useRef<HTMLDivElement>(null);
  const [isTitleEllipsed, setIsTitleEllipsed] = useState(false);

  useEffect(() => {
    const element = titleRef.current;

    if (!element) {
      return;
    }

    setIsTitleEllipsed(element.scrollHeight > element.clientHeight);
  }, [epd.name]);

  const handleCopyEpd = async () => {
    const toastId = toast.loading('Copying EPD… you will be redirected when it is ready.', { position: 'top-center' });
    try {
      setIsBusy(true);
      const newEpd = await EpdService.copyEpd(epd.id, companyAccountId!, `Copy of ${epd.name ?? ''}`, COPY_SECTIONS);
      toast.dismiss(toastId);
      history.push(EpdLinks.epd(newEpd.id));
    } catch (error) {
      toast.update(toastId, { render: PoorMansError(error), type: 'error', isLoading: false, autoClose: 5000 });
    } finally {
      setIsBusy(false);
    }
  };

  const handleConvertEpd = async (targetIsCompilerFormat: boolean) => {
    const label = targetIsCompilerFormat ? 'Converting copy to Compiler format' : 'Converting copy to Simple Wizard format';
    const toastId = toast.loading(`${label}… you will be redirected when it is ready.`, { position: 'top-center' });
    try {
      setIsBusy(true);
      const newEpd = await EpdService.copyEpd(
        epd.id,
        companyAccountId!,
        `Copy of ${epd.name ?? ''}`,
        COPY_SECTIONS,
        targetIsCompilerFormat
      );
      toast.dismiss(toastId);
      history.push(EpdLinks.epd(newEpd.id));
    } catch (error) {
      toast.update(toastId, { render: PoorMansError(error), type: 'error', isLoading: false, autoClose: 5000 });
    } finally {
      setIsBusy(false);
    }
  };

  const handleDeleteEpd = async () => {
    try {
      setIsBusy(true);
      await EpdStateService.delete(epd.id);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DASHBOARD_SEARCH] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DASHBOARD_QUICK_STATS] });
      toast.success('EPD deleted.', { position: 'top-center' });
    } catch (error) {
      toast.error(PoorMansError(error), { position: 'top-center' });
    } finally {
      setIsBusy(false);
    }
  };

  const handleViewPublishedVersion = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const publicSiteUrl = (process.env.REACT_APP_PUBLIC_SITE_URL ?? 'https://www.environdec.com').replace(/\/+$/, '');
    if (epd.friendlyUrl) {
      window.open(`${publicSiteUrl}/library/${epd.friendlyUrl}`, '_blank', 'noopener,noreferrer');
    }
  };

  const onDeleteEpd = () =>
    confirmDelete({
      caption: `Delete ${[epd.registrationNumber, epd.name].filter(Boolean).join(' ')}`,
      description: <div>The EPD data will be lost and you will not be able to restore it.</div>,
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      onConfirm: handleDeleteEpd,
    });

  const menuItems: MenuItem[] = [
    { label: 'Copy', icon: 'pi pi-copy', command: handleCopyEpd },
    epd.isCompilerFormat
      ? { label: 'Copy to Simple', icon: 'pi pi-arrow-circle-down', command: () => handleConvertEpd(false) }
      : { label: 'Copy to Compiler', icon: 'pi pi-arrow-circle-up', command: () => handleConvertEpd(true) },
    { label: 'Delete', icon: 'pi pi-trash', command: onDeleteEpd },
  ];

  return (
    <Card to={EpdLinks.epd(epd.id)} background={background}>
      <div>
        <CardTopRow>
          {epd.statusDisplayName && (
            <CardStatus status={epd.statusDisplayName}>{t(`epdWizard.epdState.status.${epd.statusDisplayName}`)}</CardStatus>
          )}

          {epd.publishedVersionId &&
            epd.friendlyUrl &&
            epd.statusDisplayName !== EPDStatusDisplayName.Depublished &&
            epd.statusDisplayName !== EPDStatusDisplayName.Deregistered && (
              <PublishedVersionButton type="button" onClick={handleViewPublishedVersion}>
                <FileMarkIcon />
                <Tooltip position="mouse">View currently published EPD version</Tooltip>
              </PublishedVersionButton>
            )}
        </CardTopRow>
        <CardHeading>{epd.registrationNumber}</CardHeading>
        <CardName ref={titleRef}>
          {epd.name}
          {isTitleEllipsed && <Tooltip position="title">{epd.name}</Tooltip>}
        </CardName>
      </div>
      <div>
        <CardFormatText>{epd.isCompilerFormat ? 'EPD Compiler format' : 'Standard EPD format'}</CardFormatText>
        <CardDate>{getCardDateText(epd)}</CardDate>
      </div>
      {isSystemAdmin && (
        <CardMenuWrapper>
          <CardMenuButton
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              menuRef.current?.toggle(e);
            }}
            disabled={isBusy}
            title="Actions"
          >
            <i className="pi pi-cog" />
          </CardMenuButton>
          <Menu ref={menuRef} model={menuItems} popup />
        </CardMenuWrapper>
      )}
    </Card>
  );
};

const CardTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const PublishedVersionButton = styled.button`
  position: relative;

  width: 24px;
  height: 24px;
  padding: 0;

  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  border: none;
  background: transparent;
  color: ${(props) => props.theme.colors.primaryGreen};

  cursor: pointer;

  &:hover {
    color: ${(props) => props.theme.colors.primaryOrange};
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

const CardMenuWrapper = styled.div`
  position: absolute;
  top: 0.4rem;
  right: 0.4rem;
`;

const CardMenuButton = styled.button`
  background: rgba(255, 255, 255, 0.8);
  border: none;
  cursor: pointer;
  padding: 3px 5px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.theme.colors.primaryBlack50};
  line-height: 1;

  &:hover {
    background: rgba(255, 255, 255, 1);
    color: ${(props) => props.theme.colors.primaryBlack};
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }

  .pi {
    font-size: 14px;
  }
`;

export default EpdCard;
